import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { take } from 'rxjs/operators';

import { EndCustomerDuplicateGroup } from 'src/app/api/models/end-customer-duplicate-group';
import { EndCustomerDuplicateMember } from 'src/app/api/models/end-customer-duplicate-member';
import { EndCustomerMergeFieldGroup } from 'src/app/api/models/end-customer-merge-field-group';
import { EndCustomerMergePreview } from 'src/app/api/models/end-customer-merge-preview';
import { EndCustomerMergeRequest } from 'src/app/api/models/end-customer-merge-request';
import { MergePickGroup } from 'src/app/api/models/merge-pick-group';
import { MergePickSide } from 'src/app/api/models/merge-pick-side';
import { EndCustomersService } from 'src/app/api/services';
import {
  ConfirmationDialogDetail,
  ConfirmationDialogModel,
} from 'src/app/components/confirmation-dialog/confirmation-dialog.component';
import { ConfirmationService } from 'src/app/shared/confirmation/confirmation.service';

const DUPLICATES_LIST_ROUTE = '/end-customers/duplicates';

/**
 * Three-column merge view for one duplicate group. The survivor is the lowest
 * member id (pinned); the FH selects the duplicate (auto-selected for a
 * two-member group), composes the surviving row field group by field group
 * (picks name a row, never values — design Decision 11), and merges or keeps
 * the group separate. Concurrency is guarded by the preview's version tokens:
 * a 409 re-fetches the preview and requires a fresh confirmation.
 */
@Component({
  selector: 'app-end-customer-merge-page',
  templateUrl: './end-customer-merge-page.component.html',
  styleUrls: ['./end-customer-merge-page.component.scss']
})
export class EndCustomerMergePageComponent implements OnInit {
  readonly PickSide = MergePickSide;

  private static readonly GROUP_LABELS: Record<MergePickGroup, string> = {
    [MergePickGroup.Name]: 'Name',
    [MergePickGroup.Address]: 'Address',
    [MergePickGroup.EinvoiceRouting]: 'E-invoice routing',
    [MergePickGroup.CreditLimit]: 'Credit limit',
    [MergePickGroup.AaaRating]: 'AAA rating',
    [MergePickGroup.CompanyForm]: 'Company form',
    [MergePickGroup.CompanySituation]: 'Company situation',
    [MergePickGroup.VatNumber]: 'VAT number',
    [MergePickGroup.Discount]: 'Discount',
  };

  businessId = '';
  group?: EndCustomerDuplicateGroup;
  /** Candidate duplicates (every member except the pinned survivor). */
  otherMembers: EndCustomerDuplicateMember[] = [];
  survivorId?: number;
  duplicateId?: number;

  preview?: EndCustomerMergePreview;
  picks: Partial<Record<MergePickGroup, MergePickSide>> = {};

  note = '';
  staleMessage = '';
  errorMessage = '';

  constructor(
    private endCustomersService: EndCustomersService,
    private route: ActivatedRoute,
    private router: Router,
    private confirmationService: ConfirmationService,
  ) { }

  ngOnInit(): void {
    this.businessId = this.route.snapshot.paramMap.get('businessId') ?? '';
    this.loadGroup();
  }

  // ---- loading ------------------------------------------------------------

  private loadGroup(): void {
    // Clear any banner from the previous pair: in an N>2 group a stale/error
    // notice must not linger over the fresh member selector after a merge that
    // leaves ≥2 members. The 409 flow re-posts its notice via selectDuplicate.
    this.staleMessage = '';
    this.errorMessage = '';
    this.endCustomersService.listEndCustomerDuplicates().pipe(take(1)).subscribe((groups) => {
      const group = groups.find(g => g.business_id === this.businessId);
      if (group === undefined) {
        // The group was resolved (merged away or kept separate) — nothing to do.
        this.router.navigate([DUPLICATES_LIST_ROUTE]);
        return;
      }
      this.group = group;
      const byId = [...group.members].sort((a, b) => a.end_customer_id - b.end_customer_id);
      this.survivorId = byId[0].end_customer_id;
      this.otherMembers = byId.slice(1);
      this.duplicateId = undefined;
      this.preview = undefined;
      if (this.otherMembers.length === 1) {
        this.selectDuplicate(this.otherMembers[0].end_customer_id);
      }
    });
  }

  selectDuplicate(duplicateId: number): void {
    if (this.survivorId === undefined) {
      return;
    }
    this.duplicateId = duplicateId;
    this.preview = undefined;
    this.staleMessage = '';
    this.errorMessage = '';
    this.endCustomersService.getEndCustomerMergePreview({
      business_id: this.businessId,
      source_id: duplicateId,        // the higher id, merged away
      destination_id: this.survivorId, // the pinned survivor (lowest id)
    }).pipe(take(1)).subscribe({
      next: (preview) => {
        this.preview = preview;
        this.initPicks(preview);
      },
      error: () => { this.errorMessage = 'Failed to load the merge preview.'; },
    });
  }

  // Default: the survivor (destination) wins, except a group where only the
  // duplicate (source) has a value — then the source is pre-selected. Applied
  // to every group so the merge request can send them all explicitly.
  private initPicks(preview: EndCustomerMergePreview): void {
    const picks: Partial<Record<MergePickGroup, MergePickSide>> = {};
    for (const fieldGroup of preview.field_groups) {
      picks[fieldGroup.group] = fieldGroup.only_side_with_value === MergePickSide.Source
        ? MergePickSide.Source
        : MergePickSide.Destination;
    }
    this.picks = picks;
  }

  // ---- pick display -------------------------------------------------------

  pickFor(group: MergePickGroup): MergePickSide {
    return this.picks[group] ?? MergePickSide.Destination;
  }

  setPick(group: MergePickGroup, side: MergePickSide): void {
    this.picks = { ...this.picks, [group]: side };
  }

  groupLabel(group: MergePickGroup): string {
    return EndCustomerMergePageComponent.GROUP_LABELS[group] ?? group;
  }

  displayValue(value: null | string | undefined): string {
    return value !== null && value !== undefined && value.trim() !== '' ? value : '—';
  }

  resultValue(fieldGroup: EndCustomerMergeFieldGroup): null | string {
    return this.pickFor(fieldGroup.group) === MergePickSide.Source
      ? fieldGroup.source_value
      : fieldGroup.destination_value;
  }

  // A group that differs on both sides with non-blank values has no safe
  // default — highlight it so the FH resolves it deliberately.
  isConflict(fieldGroup: EndCustomerMergeFieldGroup): boolean {
    return fieldGroup.differs
      && this.isPresent(fieldGroup.source_value)
      && this.isPresent(fieldGroup.destination_value);
  }

  private isPresent(value: null | string): boolean {
    return value !== null && value.trim() !== '';
  }

  memberName(endCustomerId: number): string {
    const member = this.group?.members.find(m => m.end_customer_id === endCustomerId);
    return member?.name ?? '(no name)';
  }

  // ---- actions ------------------------------------------------------------

  onMerge(): void {
    const preview = this.preview;
    if (preview === undefined) {
      return;
    }
    const details: ConfirmationDialogDetail[] = [
      { label: 'Survivor', value: `#${preview.destination_id} ${this.memberName(preview.destination_id)}` },
      { label: 'Deleted', value: `#${preview.source_id} ${this.memberName(preview.source_id)}` },
      { label: 'Contacts moved', value: String(preview.source_related.contact_count) },
      { label: 'Invoices moved', value: String(preview.source_related.invoice_count) },
      { label: 'Credit insurances moved', value: String(preview.source_related.credit_insurance_count) },
      { label: 'Comments moved', value: String(preview.source_related.comment_count) },
    ];
    const model = new ConfirmationDialogModel(
      'Merge end customers',
      `Permanently delete end customer #${preview.source_id} and move its data onto #${preview.destination_id}. `
      + 'The picked field values are written to the survivor.',
      `Merge — deletes #${preview.source_id}`,
      'Cancel',
      details,
    );
    this.confirmationService.confirm(model).pipe(take(1)).subscribe((confirmed) => {
      if (confirmed) {
        this.executeMerge(preview);
      }
    });
  }

  private executeMerge(preview: EndCustomerMergePreview): void {
    this.endCustomersService.mergeEndCustomers({
      body: {
        source_id: preview.source_id,
        destination_id: preview.destination_id,
        source_version: preview.source_version,
        destination_version: preview.destination_version,
        picks: this.buildPicks(),
      },
    }).pipe(take(1)).subscribe({
      next: () => this.onMergeSuccess(preview),
      error: (err) => this.onMergeError(err),
    });
  }

  // Send every pick group explicitly (the server would default omitted groups
  // to destination, but being explicit keeps the request self-describing).
  private buildPicks(): EndCustomerMergeRequest['picks'] {
    return {
      name: this.pickFor(MergePickGroup.Name),
      address: this.pickFor(MergePickGroup.Address),
      einvoice_routing: this.pickFor(MergePickGroup.EinvoiceRouting),
      credit_limit: this.pickFor(MergePickGroup.CreditLimit),
      aaa_rating: this.pickFor(MergePickGroup.AaaRating),
      company_form: this.pickFor(MergePickGroup.CompanyForm),
      company_situation: this.pickFor(MergePickGroup.CompanySituation),
      vat_number: this.pickFor(MergePickGroup.VatNumber),
      discount: this.pickFor(MergePickGroup.Discount),
    };
  }

  private onMergeSuccess(preview: EndCustomerMergePreview): void {
    if (preview.remaining_member_ids.length > 0) {
      // More members remain in this group — stay put, survivor pinned, and load
      // the next duplicate (loadGroup re-fetches and auto-selects for 2 left).
      this.loadGroup();
    } else {
      this.router.navigate([DUPLICATES_LIST_ROUTE]);
    }
  }

  // A stale token (409) is never retried automatically: re-fetch the preview so
  // the FH reviews the refreshed values, then confirm again.
  private onMergeError(err: unknown): void {
    if (err instanceof HttpErrorResponse && err.status === 409 && this.duplicateId !== undefined) {
      // Re-fetch first (selectDuplicate clears the banners), then post the
      // stale notice so it survives the refresh.
      this.selectDuplicate(this.duplicateId);
      this.staleMessage = 'The data changed since you reviewed it. Review the refreshed values and confirm again.';
    } else {
      this.errorMessage = 'The merge failed. Please try again.';
    }
  }

  onKeepSeparate(): void {
    const note = this.note.trim();
    const model = new ConfirmationDialogModel(
      'Keep separate',
      'Keep separate — marks these as distinct customers. Every padded business id is trimmed in place and the group leaves the duplicates list.',
      'Keep separate',
      'Cancel',
      [
        { label: 'Business ID', value: this.businessId },
        { label: 'Members', value: String(this.group?.members.length ?? 0) },
      ],
    );
    this.confirmationService.confirm(model).pipe(take(1)).subscribe((confirmed) => {
      if (!confirmed) {
        return;
      }
      this.endCustomersService.keepEndCustomersSeparate({
        business_id: this.businessId,
        body: { note: note !== '' ? note : undefined },
      }).pipe(take(1)).subscribe(() => this.router.navigate([DUPLICATES_LIST_ROUTE]));
    });
  }
}
